import React, { useMemo, useCallback, memo, useEffect, useRef } from 'react';

import { isEqual, isNumber } from 'lodash';

import {
  Button,
  Stack,
  Typography,
  WarningAmberIcon,
  ErrorOutlineIcon,
  Tooltip,
  IconButton,
  CollapseAllIcon,
  makeStyles,
} from '@gogame/components';
import DataGrid, {
  useGridApiRef,
  useGridApiContext,
  dataCyHeaderCell,
} from '@gogame/data-grid';
import { useChildComponent } from '@gogame/hooks-ts';
import { NumberValueFormatter, dateFNS, toDate } from '@gogame/intl-ts';
import {
  SET_TABLE_SETTINGS,
  useScreenDispatch,
  useScreenState,
} from '@gogame/screens';

import ConceptDetails from 'scenes/Gallery/GalleryPage/Concepts/ConceptDetails';
import CreativeDetails from 'scenes/Gallery/GalleryPage/Creatives/CreativeDetails';
import {
  REORDER_COLUMNS,
  COLLAPSE_COLUMNS,
  SET_COLOR_SCALES_COLUMNS,
  SET_PRESENTED_ROWS,
  TOGGLE_COLUMN_PERCENTAGE,
  COLLAPSE_BREAKDOWN_EXPANDED,
} from 'scenes/Trackers/Tracker/reducers';
import SmartTooltip from 'scenes/Trackers/components/SmartTooltip/SmartTooltip';

import { useUpdateDynamicData } from '../../data/mutations';
import Dimension from './Dimension';
import TrackerActions from './components/Actions';
import BulkActionsToolbar from './components/BulkActionsToolbar';
import CreativeStatus from './components/CreativeStatus/CreativeStatus';
import NoDataPlaceholder from './components/NoDataPlaceholder';
import { useChartBreakdownColors, useBulkActions } from './hooks';
import { useEditRow } from './hooks/useEditRow';
import {
  DIMENSION_DAY,
  DIMENSION_DAY_OF_THE_WEEK,
  DIMENSION_MONTH,
  DIMENSION_WEEK,
} from '../../constants';

const TABLE_HEIGHT = 700;

const EMPTY_ARR = [];
window.dataLayer = window.dataLayer || [];

const DetailPanelContent = ({ row }) => {
  const apiRef = useGridApiContext();

  const [width, setWidth] = React.useState(() => {
    const dimensions = apiRef.current.getRootDimensions();
    return dimensions.leftPinnedWidth;
  });

  const handleViewportInnerSizeChange = React.useCallback(() => {
    const dimensions = apiRef.current.getRootDimensions();
    setWidth(dimensions.leftPinnedWidth);
  }, [apiRef]);

  React.useEffect(() => {
    return apiRef.current.subscribeEvent(
      'viewportInnerSizeChange',
      handleViewportInnerSizeChange
    );
  }, [apiRef, handleViewportInnerSizeChange]);

  return (
    <Stack
      sx={{
        py: 2,
        height: '100%',
        boxSizing: 'border-box',
        position: 'sticky',
        left: 0,
        width: `${width}px`,
        padding: '0 8px',
      }}
      direction={'column'}
      justifyContent={'center'}
    >
      <div>
        <Button onClick={() => row?.nextPage?.()} sx={{ width: '100%' }}>
          Load more
        </Button>
      </div>
    </Stack>
  );
};

const sx = {
  '& .MuiDataGrid-detailPanel': {
    overflow: 'visible',
  },
};

const useStyles = makeStyles(() => ({
  dimensionHeaderWithCollapseAll: {
    paddingLeft: '0 !important',
  },
}));

const TrackerDataTable = ({
  _focusedRow,
  toolbar,
  compareMode,
  dimensionName,
  processAsync,
  columns = EMPTY_ARR,
  withDrillDown,
  breakdownTitle,
  nextDimension,
  columnWidthGetter,
  isShowActions,
  totalCount,
  rows = EMPTY_ARR,
  isLoading,
  isFetching,
  ready,
  drillPath,
  breakdownPath,
  tableSettings,
  screenStateColumns,
  dimensionSearchQuery,
  filtered,
  presentedRows,
  fullScreen,
  onToggleFullScreen,
}) => {
  const [childComponent, setChildComponent] = useChildComponent();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();

  const handleReorderColumns = async (order) => {
    await dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapseColumn = async (collapsed) => {
    await dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  const handleChangeColorScalesColumns = async ({ field, isColorScale }) => {
    if (!isColorScale) {
      window.dataLayer.push({
        event: 'colorScaled',
        colorScaledMetric: field,
      });
    }
    await dispatch({
      type: SET_COLOR_SCALES_COLUMNS,
      payload: { columnName: field, isColorScale },
    });
  };

  const handleTogglePercentage = ({ field }) => {
    dispatch({
      type: TOGGLE_COLUMN_PERCENTAGE,
      payload: field,
    });

    window.dataLayer.push({
      event: 'togglePercentage',
      togglePercentageField: field,
      togglePercentageValue: !screenStateColumns.percentage.includes(field),
    });
  };

  const handleChangeSettings = async (settings) => {
    await dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };
  const apiRef = useGridApiRef();

  const dimension = useMemo(() => {
    return drillPath?.[drillPath.length - 1]?.type;
  }, [drillPath]);

  const openRows = useMemo(() => {
    const opened = [];
    breakdownPath.forEach(({ openRows }) => {
      opened.push(...openRows);
    });
    return opened;
  }, [breakdownPath]);

  const { hasChartBreakdown, breakdownColorGetter } =
    useChartBreakdownColors(rows);

  const [bulkActions, getBulkCol] = useBulkActions(columns);
  const [updateDynamicData] = useUpdateDynamicData();
  const handleEdit = (rows) => {
    apiRef.current.updateRows(rows);
  };

  const { onEditCell, onEditBulkCells } = useEditRow({
    onChangeRows: handleEdit,
    dimensionName,
    dimension,
    updateDynamicData,
  });

  const handleEditBulk = async (
    bulkAction,
    value,
    { selectedValues, reset }
  ) => {
    try {
      await onEditBulkCells(
        {
          col: getBulkCol(bulkAction),
          value,
          rows: rows.filter(({ id }) => selectedValues.includes(id)),
        },
        processAsync
      );
    } catch (e) {
    } finally {
      reset();
    }
  };

  const handleIsRowSelectable = useCallback(({ row }) => {
    const { hierarchy } = row;
    return hierarchy?.length === 1;
  }, []);

  const handleOpenConcept = useCallback((id) => {
    setChildComponent(ConceptDetails, { id });
  }, []);

  const handleOpenCreative = useCallback((id) => {
    setChildComponent(CreativeDetails, { id });
  }, []);

  const columnsState = screenStateColumns;

  useEffect(() => {
    if (Array.isArray(rows)) {
      openRows.forEach((id) => {
        const rowNode = apiRef.current.getRowNode(id);
        if (rowNode?.type === 'group') {
          apiRef.current.setRowChildrenExpansion(id, true);
        }
      });
    }
  }, [openRows, rows]);
  const handlePresentSelected = (selectedRows) => {
    dispatch({
      type: SET_PRESENTED_ROWS,
      payload: getRowsDimensionIds(selectedRows),
    });
  };

  const handleClearPresentedRows = () => {
    dispatch({
      type: SET_PRESENTED_ROWS,
      payload: [],
    });
  };

  const getTreeDataPath = useCallback((row) => {
    return row.hierarchy;
  }, []);

  const presentedRowsKey = getPresentedRowsKey(presentedRows);

  const selectedRowsToolbarActions = useCallback(
    ({ selectedValues, reset }) => {
      return (
        <BulkActionsToolbar
          bulkActions={bulkActions}
          onEditBulk={handleEditBulk}
          selectedValues={selectedValues}
          onPresentSelected={handlePresentSelected}
          reset={reset}
          disablePresentRows={
            presentedRowsKey ===
            getPresentedRowsKey(getRowsDimensionIds(selectedValues))
          }
        />
      );
    },
    [bulkActions, handlePresentSelected, presentedRowsKey]
  );

  const detailPanelExpandedRowIds = useMemo(() => {
    return rows.filter(({ canLoadMore }) => canLoadMore).map(({ id }) => id);
  }, [rows]);

  useEffect(() => {
    if (rows?.length) {
      const focusedRow = rows.find(({ _focused }) => _focused);

      if (focusedRow) {
        window.api = apiRef.current;

        setTimeout(() => {
          const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(
            focusedRow.id
          );
          if (rowIndex !== undefined) {
            apiRef.current.scrollToIndexes({ rowIndex, colIndex: 0 });
          }
        }, 0);
      }
    }
  }, [rows, _focusedRow]);
  const getDetailPanelContent = React.useCallback(
    ({ row }) => <DetailPanelContent row={row} />,
    []
  );

  const getDetailPanelHeight = React.useCallback(() => 50, []);

  const decorationModel = React.useMemo(() => {
    return {
      ...columnsState.percentage.reduce((m, col) => {
        return {
          ...m,
          [col]: function ({ value, row, api }) {
            let totalRow;
            if (isSafeRow(row) && !row.isTotalValue) {
              const totalId = row.id.replace(row.Dimension.id, 'Totals');
              totalRow = api.getRow(totalId);
              if (!totalRow) {
                const rowNode = api.getRowNode(row.id);
                if (rowNode) {
                  totalRow = api.getRow(rowNode.parent);
                }
              }
            }

            return isSafeRow(totalRow)
              ? addPercentValue(value, totalRow[col])
              : value;
          },
        };
      }, {}),
    };
  }, [columnsState.percentage]);

  const pinnedRows = useMemo(() => {
    return rows.filter((row) => row.pinned);
  }, [rows]);

  const handleCollapseBreakdownExpanded = (e) => {
    e.stopPropagation();
    e.preventDefault();

    dispatch({
      type: COLLAPSE_BREAKDOWN_EXPANDED,
    });
  };

  const showCollapseAllRowsRef = useRef(false);

  showCollapseAllRowsRef.current = !!openRows.length;

  const classes = useStyles();

  const hasDayBreakdown = screenState.breakdownPath?.some((breakpoint) => {
    const isDateDimension = [
      DIMENSION_DAY,
      DIMENSION_WEEK,
      DIMENSION_MONTH,
      DIMENSION_DAY_OF_THE_WEEK,
    ].includes(breakpoint.breakdownId);
    return isDateDimension && breakpoint.openRows?.length > 0;
  });

  return (
    <React.Fragment>
      <DataGrid
        rows={rows}
        toolbar={toolbar}
        selectedRowsToolbarActions={selectedRowsToolbarActions}
        apiRef={apiRef}
        count={totalCount}
        loading={isLoading}
        fetching={isFetching}
        ready={ready}
        isRowSelectable={handleIsRowSelectable}
        withColorScale={true}
        checkboxSelection={true}
        hasChildrenOnServer={true}
        onChangeSettings={handleChangeSettings}
        getTreeDataPath={getTreeDataPath}
        storageKey={`Tracker: ${screenState.filtersCollection.length > 1 ? 'compare' : ''}`}
        treeData
        tableSettings={tableSettings}
        columnsState={columnsState}
        onReorder={handleReorderColumns}
        onCollapse={handleCollapseColumn}
        pinnedRows={pinnedRows}
        minHeight={TABLE_HEIGHT}
        lazyLoad
        coloredRow={hasChartBreakdown}
        colorGetter={breakdownColorGetter}
        onChangeColorScalesColumns={handleChangeColorScalesColumns}
        onTogglePercentage={handleTogglePercentage}
        searchQuery={dimensionSearchQuery}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        rowThreshold={0}
        presentedRows={presentedRows}
        onClearPresentedRows={handleClearPresentedRows}
        sx={sx}
        notInitedLabel={<NoDataPlaceholder />}
        filtered={filtered}
        decorationModel={decorationModel}
        fullScreen={fullScreen}
        onToggleFullScreen={onToggleFullScreen}
        sortingTooltip={
          hasDayBreakdown
            ? "Sorting wasn't applied to date dimension rows, as they are chronologically ordered by default. Please try using a color scale instead."
            : undefined
        }
      >
        <DataGrid.Column
          onOpenConcept={handleOpenConcept}
          onOpenCreative={handleOpenCreative}
          withDrillDown={withDrillDown}
          nextDimension={nextDimension}
          renderCell={(params) => {
            const { colDef } = params;
            return (
              <Dimension
                onOpenConcept={colDef.onOpenConcept}
                onOpenCreative={colDef.onOpenCreative}
                withDrillDown={colDef.withDrillDown}
                nextDimension={colDef.nextDimension}
                {...params}
              />
            );
          }}
          field={'Dimension'}
          fixed={true}
          groupingColDef
          disableReorder={true}
          sortable={true}
          width={columnWidthGetter({ name: 'Dimension' })}
          minWidth={80}
          maxWidth={400}
          dimensionName={dimensionName}
          breakdownTitle={breakdownTitle}
          headerClassName={
            showCollapseAllRowsRef.current
              ? classes.dimensionHeaderWithCollapseAll
              : null
          }
          renderHeader={(params) => {
            const { colDef } = params;
            const { dimensionName, breakdownTitle } = colDef;
            return (
              <React.Fragment>
                {showCollapseAllRowsRef.current ? (
                  <IconButton
                    onClick={handleCollapseBreakdownExpanded}
                    icon={<CollapseAllIcon />}
                    tooltip={'Collapse all'}
                    fontSize={18}
                  />
                ) : null}
                <Typography
                  lineClamp={2}
                  disableTooltip={true}
                  data-cy={dataCyHeaderCell}
                  variant={'caption'}
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    fontWeight: 'inherit',
                  }}
                >
                  {dimensionName
                    ? `${dimensionName} ${
                        breakdownTitle ? ` /  ${breakdownTitle}` : ''
                      }`
                    : ''}
                </Typography>
              </React.Fragment>
            );
          }}
        />
        {columns.map(
          ({
            name,
            type,
            displayName,
            tooltip,
            hint,
            fullDisplayName,
            displayFormula,
            showGlossary,
            glossaryItems,
            isHeuristics,
            isHighlightLower,
            isExpirationDate,
            editable,
          }) => {
            const col = {
              field: name,
              type: type,
              headerName: displayName,
              renderHeader: ({ colDef }) => {
                if (!colDef) {
                  /* eslint-disable-next-line */
                  console.log('!colDef debug', {
                    col,
                    colDef,
                    name,
                    type,
                    displayName,
                  });
                  return null;
                }
                const {
                  headerName,
                  fullDisplayName,
                  displayFormula,
                  hint,
                  showGlossary,
                  glossaryItems,
                  align,
                } = colDef;

                return (
                  <SmartTooltip
                    hoverDelay={1000}
                    anchor={
                      <Typography
                        lineClamp={2}
                        disableTooltip={true}
                        data-cy={dataCyHeaderCell}
                        variant={'caption'}
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-all',
                          fontWeight: 'inherit',
                        }}
                      >
                        {headerName}
                      </Typography>
                    }
                    name={fullDisplayName || headerName}
                    formula={displayFormula}
                    hint={hint}
                    showGlossary={showGlossary}
                    glossaryItems={glossaryItems}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: align,
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: align,
                    }}
                  />
                );
              },
              width: columnWidthGetter({ name, type }),
              tooltip,
              hint,
              fullDisplayName,
              displayFormula,
              showGlossary,
              glossaryItems,
              dotted: isHeuristics,
              isHighlightLower: isHighlightLower,
              isExpirationDate: isExpirationDate,
              minWidth: 80,
              maxWidth: 400,
              onEditCell,
              editableCell: editable,
              withColorScale: ['percent', 'currency', 'number'].includes(type),
              withPercentage: ['currency', 'number'].includes(type),
              getValue: (v) => v?.value,
              getCompareValue: (v) => v?.compareValue,
              getDifferenceValue: (v) => v?.difference,
              getPercentValue: (v) => v?.percentValue,
              getComparePercentValue: (v) => v?.comparePercentValue,
              getValueColor: (v) => getValueColor(v?.color),
              getCompareValueColor: (v) => getValueColor(v?.compareColor),
              getIcon: (value, _, column) => {
                if (value?.fraudDepositAmount) {
                  return <FraudWarning value={value?.fraudDepositAmount} />;
                }

                if (column?.isExpirationDate && value?.value) {
                  const diffDays = dateFNS.differenceInDays(
                    dateFNS.startOfDay(toDate(value.value)),
                    dateFNS.startOfDay(new Date())
                  );

                  if (diffDays <= 0) {
                    return <ErrorIcon tooltip={'Deep link expired'} />;
                  } else if (diffDays <= 30) {
                    return <WarningIcon tooltip={'Deep link expires soon'} />;
                  }
                }

                return null;
              },
              getCompareIcon: (value) => {
                if (value?.compareFraudDepositAmount) {
                  return (
                    <FraudWarning value={value?.compareFraudDepositAmount} />
                  );
                }
                return null;
              },
            };

            if (isHeuristics) {
              col.getTooltip = () => 'Based on heuristics and not actual data';
            }

            if (type === 'creative_status') {
              // it works for all statuses, not only for creatives
              col.renderCell = (p) => <CreativeStatus {...p} />;
            }
            if (type === 'rating') {
              col.type = 'number';
            }
            if (type === 'item_collection') {
              col.type = 'commaList';
              col.unitsLabel = displayName.replace('Used In ', '');
              col.limit = 1;
              col.getItemValue = (v) => v;
              col.placeholder = '-';
            }

            return <DataGrid.Column {...col} key={name} />;
          }
        )}
        {isShowActions && (
          <DataGrid.Column
            actionsColDef
            type={'actions'}
            actionsComponent={TrackerActions}
          />
        )}
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

function getPresentedRowsKey(ids) {
  return [...ids].sort().join(',');
}

function getRowsDimensionIds(selectedRows) {
  return selectedRows.map((rowId) => {
    const levels = rowId.split('///');
    const [, dimensionId] = splitInPair(levels.pop());
    return dimensionId;
  });
}

function splitInPair(inputString) {
  const colonIndex = inputString.indexOf(':');
  return [inputString.slice(0, colonIndex), inputString.slice(colonIndex + 1)];
}
// export default TrackerDataTableContent;
export default memo(TrackerDataTable, (oldP, newP) => {
  const res =
    isEqual(oldP.toolbar, newP.toolbar) &&
    isEqual(oldP.rows, newP.rows) &&
    isEqual(oldP?.columnsState, newP?.columnsState) &&
    isEqual(oldP.tableSettings, newP.tableSettings) &&
    oldP.coloredRow === newP.coloredRow &&
    oldP.isLoading === newP.isLoading &&
    oldP.isFetching === newP.isFetching &&
    oldP.fullScreen === newP.fullScreen &&
    oldP.searchQuery === newP.searchQuery;

  return res;
});

function addPercentValue(metricValue, metricTotals) {
  return {
    ...metricValue,
    ...(isNumber(metricValue.value)
      ? { percentValue: getPercent(metricValue.value, metricTotals.value) }
      : null),
    ...(isNumber(metricValue.compareValue)
      ? {
          comparePercentValue: getPercent(
            metricValue.compareValue,
            metricTotals.compareValue
          ),
        }
      : null),
  };
}

function WarningIcon({ tooltip }) {
  return (
    <Tooltip noMaxWidth title={<span>{tooltip}</span>}>
      <WarningAmberIcon sx={{ fontSize: 16 }} color={'warning'} />
    </Tooltip>
  );
}

function ErrorIcon({ tooltip }) {
  return (
    <Tooltip noMaxWidth title={<span>{tooltip}</span>}>
      <ErrorOutlineIcon sx={{ fontSize: 16 }} color={'error'} />
    </Tooltip>
  );
}

function FraudWarning({ value }) {
  return (
    <WarningIcon
      tooltip={
        <span>
          <NumberValueFormatter.Currency value={value} /> are missing from
          deposit amount due to fraud filter
        </span>
      }
    />
  );
}

function getValueColor(id) {
  switch (id) {
    case 0:
      return 'success';
    case 2:
      return 'error';
    case 1:
      return 'warning';
  }
}

function getPercent(value, total) {
  return (100 * value) / total;
}

function isSafeRow(row) {
  return row?.id !== undefined;
}
